/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `timestamp_correlation` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class TimestampCorrelationMesg : Mesg {
    public constructor() : super("TimestampCorrelation", Profile.MesgNum.TIMESTAMP_CORRELATION)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Whole second part of UTC timestamp at the time the system timestamp was recorded. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** fractional_timestamp, in s. Fractional part of the UTC timestamp at the time the system timestamp was recorded. */
    public var fractionalTimestamp: Double?
        get() = getFieldValue(FRACTIONAL_TIMESTAMP_FIELD_NUM) as? Double
        set(value) = setFieldValue(FRACTIONAL_TIMESTAMP_FIELD_NUM, value)

    /** system_timestamp, in s. Whole second part of the system timestamp */
    public var systemTimestamp: Instant?
        get() = (getFieldValue(SYSTEM_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(SYSTEM_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** fractional_system_timestamp, in s. Fractional part of the system timestamp */
    public var fractionalSystemTimestamp: Double?
        get() = getFieldValue(FRACTIONAL_SYSTEM_TIMESTAMP_FIELD_NUM) as? Double
        set(value) = setFieldValue(FRACTIONAL_SYSTEM_TIMESTAMP_FIELD_NUM, value)

    /** local_timestamp, in s. timestamp epoch expressed in local time used to convert timestamps to local time */
    public var localTimestamp: Instant?
        get() = (getFieldValue(LOCAL_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(LOCAL_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond part of the UTC timestamp at the time the system timestamp was recorded. */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** system_timestamp_ms, in ms. Millisecond part of the system timestamp */
    public var systemTimestampMs: UShort?
        get() = getFieldValue(SYSTEM_TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SYSTEM_TIMESTAMP_MS_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val FRACTIONAL_TIMESTAMP_FIELD_NUM: UByte = 0u
        public const val SYSTEM_TIMESTAMP_FIELD_NUM: UByte = 1u
        public const val FRACTIONAL_SYSTEM_TIMESTAMP_FIELD_NUM: UByte = 2u
        public const val LOCAL_TIMESTAMP_FIELD_NUM: UByte = 3u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 4u
        public const val SYSTEM_TIMESTAMP_MS_FIELD_NUM: UByte = 5u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("FractionalTimestamp", 0u, BaseType.UINT16, 32768.0, 0.0, "s", false)
            fields[1] = Field("SystemTimestamp", 1u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[2] = Field("FractionalSystemTimestamp", 2u, BaseType.UINT16, 32768.0, 0.0, "s", false)
            fields[3] = Field("LocalTimestamp", 3u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[4] = Field("TimestampMs", 4u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[5] = Field("SystemTimestampMs", 5u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            return fields
        }
    }
}
